import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Layers, Type, ChevronDown, ChevronUp, X } from 'lucide-react';
import { tokensToWords, WORDS_HELP } from '../../lib/tokensToWords';

/**
 * SheetPicker — bloque (B) "Sheet Selection" extraído de ContextControls.
 * Chips de hojas seleccionadas (la activa siempre primera y no removible) +
 * <details> con las hojas disponibles para agregar. Hoy vive dentro del
 * Popover del icono de Hojas del toolbar del header (T-146).
 *
 * Mismo markup y misma lógica que tenía embebido en ContextControls.
 */
export function SheetPicker({ sheets, activeSheetId, selectedSheets, setSelectedSheets }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Hojas seleccionadas ({selectedSheets.length})</label>
      <div className="flex flex-wrap gap-2">
        {sheets
          .filter(s => selectedSheets.includes(s.id))
          // La hoja actual SIEMPRE primero.
          .sort((a, b) => (a.id === activeSheetId ? -1 : b.id === activeSheetId ? 1 : 0))
          .map((sheet) => {
            const isActive = sheet.id === activeSheetId;
            return (
              <button
                key={sheet.id}
                data-testid={`select-sheet-${sheet.id}`}
                disabled={isActive}
                title={isActive ? 'Hoja actual (siempre incluida)' : 'Quitar del contexto'}
                onClick={() => {
                  if (isActive) return; // la actual es fija, no removible
                  setSelectedSheets(selectedSheets.filter(id => id !== sheet.id));
                }}
                className={`px-3 py-1 rounded-full text-sm transition-all ${
                  isActive
                    ? 'bg-sky-600 text-white cursor-default ring-2 ring-sky-300 dark:ring-sky-700'
                    : 'bg-sky-500 text-white hover:bg-sky-600'
                }`}
              >
                {sheet.name}{isActive ? ' · actual' : ' ✕'}
              </button>
            );
          })}
      </div>

      {sheets.filter(s => !selectedSheets.includes(s.id)).length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-slate-600 dark:text-slate-300 cursor-pointer hover:text-slate-800 dark:hover:text-slate-100 select-none">
            + {sheets.filter(s => !selectedSheets.includes(s.id)).length} hoja(s) disponible(s)
          </summary>
          <div className="flex flex-wrap gap-2 mt-2 pl-2">
            {sheets.filter(s => !selectedSheets.includes(s.id)).map((sheet) => (
              <button
                key={sheet.id}
                data-testid={`add-sheet-${sheet.id}`}
                onClick={() => {
                  setSelectedSheets([...selectedSheets, sheet.id]);
                }}
                className="px-3 py-1 rounded-full text-sm transition-all bg-white/50 hover:bg-white/70 dark:bg-slate-700/60 dark:hover:bg-slate-600/70"
              >
                + {sheet.name}
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * ContextControls — la sección (C) "Context Info Card": tarjeta de info de
 * tokens + imágenes a describir.
 *
 * T-146: el Select de contexto (bloque A) y el SheetPicker (bloque B) ya NO
 * viven aquí — se movieron a 3 iconos excluyentes en el toolbar del header.
 * Los props se mantienen intactos por compatibilidad (algunos ya no se usan
 * aquí pero el caller los sigue pasando). La tarjeta de selección (modo
 * 'selection') sigue mostrándose aquí.
 * T-239: el checkbox de comentarios se movió al popover de adjuntos en index.js.
 */
export function ContextControls({
  context,
  setContext,
  sheets,
  activeSheetId,
  selectedSheets,
  setSelectedSheets,
  selection,
  setSelection,
  isSelectionExpanded,
  setIsSelectionExpanded,
  getContextLabel,
  getContextTokens,
  getMessageTokens,
  getTotalTokens,
  input,
}) {
  return (
    <>
      {/* Context Info Card */}
      {(context !== 'none' && (
        (context === 'full') ||
        (context === 'sheets' && selectedSheets.length > 0) ||
        (context === 'selection' && selection)
      )) && (
        <div className="mt-3">
          <div className="p-3 bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 rounded-lg space-y-2" data-testid="context-info-card">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {context === 'full' && <FileText className="w-4 h-4 text-sky-600 dark:text-sky-400" />}
                  {context === 'sheets' && <Layers className="w-4 h-4 text-sky-600 dark:text-sky-400" />}
                  {context === 'selection' && <Type className="w-4 h-4 text-sky-600 dark:text-sky-400" />}
                  <span className="text-sm font-medium text-sky-900 dark:text-sky-200">
                    {getContextLabel()}
                  </span>
                </div>
                {context === 'selection' && selection && (
                  <div className="mt-2">
                    <button
                      onClick={() => setIsSelectionExpanded(!isSelectionExpanded)}
                      className="flex items-center gap-1 text-xs text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-100 transition-colors"
                    >
                      {isSelectionExpanded ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Ocultar texto
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          Ver texto completo
                        </>
                      )}
                    </button>
                    {isSelectionExpanded && (
                      <div className="text-xs text-sky-700 dark:text-sky-300 bg-white/50 dark:bg-slate-800/60 p-2 rounded mt-2 max-h-32 overflow-y-auto">
                        {selection}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  if (context === 'selection') {
                    setSelection('');
                    setIsSelectionExpanded(false);
                  }
                  setContext('none');
                }}
                className="text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-200 ml-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Token Statistics */}
            <div className="flex items-center gap-4 text-xs text-sky-700 dark:text-sky-300 pt-2 border-t border-sky-200 dark:border-sky-800">
              <div className="flex items-center gap-1">
                <span className="font-medium">Contexto:</span>
                <span className="bg-white/70 dark:bg-slate-800/70 px-2 py-0.5 rounded" title={WORDS_HELP}>≈ {tokensToWords(getContextTokens()).toLocaleString()} palabras</span>
              </div>
              {input && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">Mensaje:</span>
                  <span className="bg-white/70 dark:bg-slate-800/70 px-2 py-0.5 rounded" title={WORDS_HELP}>≈ {tokensToWords(getMessageTokens()).toLocaleString()} palabras</span>
                </div>
              )}
              <div className="flex items-center gap-1 ml-auto">
                <span className="font-medium">Total:</span>
                <span className="bg-sky-600 text-white px-2 py-0.5 rounded font-semibold" title={WORDS_HELP}>
                  ≈ {tokensToWords(getTotalTokens()).toLocaleString()} palabras
                </span>
              </div>
            </div>

          </div>
        </div>
      )}

    </>
  );
}

/**
 * DescribeImagesBar — la barra violeta "🖼 Enviar imágenes a describir".
 * Se extrae de ContextControls para vivir como una barra delgada justo encima
 * del input del mensaje. Se auto-oculta si no hay imágenes en el contexto.
 */
export function DescribeImagesBar({
  imagesInScope = [],
  describeImages,
  setDescribeImages,
  excludedImageSrcs,
  toggleImageExcluded,
  imagesToDescribe = [],
  imageEstimate,
}) {
  if (imagesInScope.length === 0) return null;
  return (
    <div className="">
      <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-3 space-y-2">
        <label className="flex items-center gap-2 text-xs font-medium text-violet-900 dark:text-violet-200 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="describe-images-checkbox"
            checked={!!describeImages}
            onChange={(e) => setDescribeImages(e.target.checked)}
            className="accent-violet-600"
          />
          🖼 Enviar imágenes a describir ({imagesInScope.length} en contexto)
        </label>

        {describeImages && (
          <>
            <p className="text-[11px] text-violet-800/80 dark:text-violet-300/80">
              {imagesToDescribe.length === 0
                ? 'Todas las seleccionadas ya tienen descripción (sin costo).'
                : imageEstimate
                  ? `Se describirán ${imagesToDescribe.length} imagen(es) nueva(s) · ≈ ${imageEstimate.credits} crédito(s) estimado(s).`
                  : `Se describirán ${imagesToDescribe.length} imagen(es) nueva(s).`}
            </p>
            <p className="text-[10px] text-violet-700/70 dark:text-violet-400/70">
              Desmarca las que NO quieras enviar (por defecto van todas). Si desactivas
              el envío, la IA solo usará los nombres.
            </p>
            <div className="max-h-28 overflow-y-auto space-y-1 pt-1">
              {imagesInScope.map((img, idx) => (
                <label
                  key={`${img.src}-${idx}`}
                  className="flex items-center gap-2 text-[11px] text-violet-900 dark:text-violet-100 cursor-pointer select-none"
                  title={img.src}
                >
                  <input
                    type="checkbox"
                    checked={!excludedImageSrcs?.has(img.src)}
                    onChange={() => toggleImageExcluded(img.src)}
                    className="accent-violet-600"
                  />
                  <span className="truncate flex-1">{img.name}</span>
                  {img.hasDescription && (
                    <span className="text-[9px] text-green-700 dark:text-green-400 shrink-0">✓ descrita</span>
                  )}
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * HistoryLimitControl — la sección (B) "Historial de este chat":
 * el Select del nº de mensajes de memoria + su nota.
 *
 * Independiente del contexto de documento (puedes mandar 0 de historial y
 * aún así incluir hojas). Default 0. Mismo markup que antes, movido aquí para
 * vivir dentro de un Popover separado del de Contexto.
 */
export function HistoryLimitControl({ historyLimit = 10, setHistoryLimit }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Historial de este chat</label>
      <Select value={String(historyLimit)} onValueChange={setHistoryLimit}>
        <SelectTrigger data-testid="history-limit-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="0">Sin historial</SelectItem>
          <SelectItem value="10">Últimos 10 mensajes (por defecto)</SelectItem>
          <SelectItem value="25">Últimos 25 mensajes</SelectItem>
          <SelectItem value="50">Últimos 50 mensajes</SelectItem>
          <SelectItem value="100">Últimos 100 mensajes</SelectItem>
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Se aplica solo a esta conversación. Más mensajes = la IA recuerda más, pero lee más palabras y consume más créditos.
      </p>
    </div>
  );
}

/**
 * ContextSelector (default export) — compone las dos secciones para
 * retro-compatibilidad. Hoy sólo index.js lo usaba, y ahora usa los dos
 * sub-componentes directamente dentro de Popovers; este default se mantiene
 * por si algún sitio externo lo importa.
 */
const ContextSelector = (props) => (
  <>
    <ContextControls {...props} />
    <HistoryLimitControl
      historyLimit={props.historyLimit}
      setHistoryLimit={props.setHistoryLimit}
    />
  </>
);

export default ContextSelector;
